import { useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { FormikPhoneField, FormikTextField } from '../../../../../../components/FormikFields'
import { ModalPanel, Section, TabSwitch } from '../../../../../../components/ModalSidebar'
import { CompanyAccessFields, LoginOptionsField, PermissionFields } from '../../../SystemUserFields'
import { TAB_KEYS } from '../../usePanelTabs'
import styles from './EditSystemUserForm.module.scss'
import SaveErrorBanner from './SaveErrorBanner/SaveErrorBanner'

/**
 * Edit System User fields laid out as the System User tab, with Cancel and Save in the panel footer.
 */
function EditSystemUserForm({
  tabs,
  isOwner = false,
  canEnterDetailsByHand = false,
  hasManagePermission = false,
  isLoginOptionsLocked = false,
  isSaving = false,
  hasError = false,
  canSave = true,
  companyAccessOptions,
  savedCollectionName = null,
  isFetchingCustomerCollections = false,
  setCollectionToView,
  permissionLevels,
  permissionEntities,
}) {
  const intl = useIntl()
  const { submitForm, values } = useFormikContext()
  const hasCompanyAccess = values.customers?.length > 0 || !!values.collectionId
  const phoneInputProps = useMemo(() => ({ 'aria-label': intl.formatMessage({ id: 'FORM.PHONE' }) }), [intl])

  const saveButton = useMemo(
    () => ({
      id: 'edit-user-submit-btn',
      label: 'GENERAL.SAVE',
      variant: 'primary',
      onClick: submitForm,
      disabled: isSaving || isOwner || !canSave || !hasCompanyAccess,
    }),
    [submitForm, isSaving, isOwner, canSave, hasCompanyAccess],
  )

  const handleFormSubmit = useCallback(
    (event) => {
      event.preventDefault()
      submitForm()
    },
    [submitForm],
  )

  return (
    <ModalPanel leftButton="cancel" rightButton={saveButton} tabs={tabs}>
      <form className={styles.form} onSubmit={handleFormSubmit} noValidate>
        {hasError && <SaveErrorBanner />}

        <TabSwitch tab={TAB_KEYS.SYSTEM_USER}>
          <div className="px-4 py-4">
            <Section title="SYSTEM_USER_PANEL.ACCOUNT">
              {/* Inline fields render their title outside a <label>, so each input is named with aria-label. */}
              <FormikTextField
                id="firstName"
                title="FORM.FIRST_NAME"
                aria-label={intl.formatMessage({ id: 'FORM.FIRST_NAME' })}
                placeholder={intl.formatMessage({ id: 'PLACEHOLDER.ENTER_FIRST_NAME' })}
                required
                inline
                disabled={isOwner}
                disablePasswordManager
              />
              <FormikTextField
                id="lastName"
                title="FORM.LAST_NAME"
                aria-label={intl.formatMessage({ id: 'FORM.LAST_NAME' })}
                placeholder={intl.formatMessage({ id: 'PLACEHOLDER.ENTER_LAST_NAME' })}
                required
                inline
                disabled={isOwner}
                disablePasswordManager
              />
              <FormikTextField
                id="email"
                title="FORM.EMAIL"
                aria-label={intl.formatMessage({ id: 'FORM.EMAIL' })}
                inline
                disabled
                disablePasswordManager
              />
              {canEnterDetailsByHand && (
                <>
                  <FormikTextField
                    id="jobTitle"
                    title="FORM.JOB_TITLE"
                    aria-label={intl.formatMessage({ id: 'FORM.JOB_TITLE' })}
                    placeholder={intl.formatMessage({ id: 'PLACEHOLDER.ENTER_JOB_TITLE' })}
                    inline
                    disabled={isOwner}
                    disablePasswordManager
                  />
                  <FormikTextField
                    id="department"
                    title="FORM.DEPARTMENT"
                    aria-label={intl.formatMessage({ id: 'FORM.DEPARTMENT' })}
                    placeholder={intl.formatMessage({ id: 'PLACEHOLDER.ENTER_DEPARTMENT' })}
                    inline
                    disabled={isOwner}
                    disablePasswordManager
                  />
                  <FormikPhoneField
                    id="phone"
                    title="FORM.PHONE"
                    inputProps={phoneInputProps}
                    placeholder={intl.formatMessage({ id: 'PLACEHOLDER.ENTER_PHONE' })}
                    inline
                    disabled={isOwner}
                  />
                  <FormikTextField
                    id="officeLocation"
                    title="FORM.OFFICE_LOCATION"
                    aria-label={intl.formatMessage({ id: 'FORM.OFFICE_LOCATION' })}
                    placeholder={intl.formatMessage({ id: 'PLACEHOLDER.ENTER_OFFICE_LOCATION' })}
                    inline
                    disabled={isOwner}
                    disablePasswordManager
                  />
                </>
              )}
            </Section>

            {hasManagePermission && (
              <Section title="SYSTEM_USER_PANEL.SIGN_IN">
                <LoginOptionsField isDisabled={isLoginOptionsLocked} />
              </Section>
            )}

            <Section title="SYSTEM_USER_PANEL.COMPANY_ACCESS">
              <CompanyAccessFields
                companyAccessOptions={companyAccessOptions}
                savedCollectionName={savedCollectionName}
                isOwner={isOwner}
                isFetchingCustomerCollections={isFetchingCustomerCollections}
                setCollectionToView={setCollectionToView}
              />
            </Section>

            <Section title="SYSTEM_USER_PANEL.PERMISSIONS" showSeparatorLine={false}>
              <p className="text-color-secondary">
                <FormattedMessage id="USERPERMISSION.permissioninfo" />
              </p>
              <PermissionFields
                permissionLevels={permissionLevels}
                permissionEntities={permissionEntities}
                isOwner={isOwner}
              />
            </Section>
          </div>
        </TabSwitch>
      </form>
    </ModalPanel>
  )
}

EditSystemUserForm.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.node })).isRequired,
  isOwner: PropTypes.bool,
  canEnterDetailsByHand: PropTypes.bool,
  hasManagePermission: PropTypes.bool,
  isLoginOptionsLocked: PropTypes.bool,
  isSaving: PropTypes.bool,
  hasError: PropTypes.bool,
  /** False when the user's current settings could not be loaded, so saving would overwrite them */
  canSave: PropTypes.bool,
  companyAccessOptions: PropTypes.shape({}).isRequired,
  /** Name of the user's saved collection from the system users list */
  savedCollectionName: PropTypes.string,
  isFetchingCustomerCollections: PropTypes.bool,
  setCollectionToView: PropTypes.func.isRequired,
  permissionLevels: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })).isRequired,
  permissionEntities: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, module_name: PropTypes.string }))
    .isRequired,
}

export default React.memo(EditSystemUserForm)
